import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, Save, Lock } from "lucide-react";

const ACTIONS = ["view", "create", "update", "approve", "post", "export", "manage"];
const ACTION_LABEL = { view: "View", create: "Create", update: "Edit", approve: "Approve", post: "Post", export: "Export", manage: "Manage" };
const lastSeg = (c) => c.split(".").pop();

export default function Roles() {
  const { user } = useAuth();
  const isOwnerActor = user?.role === "OWNER_CEO";
  const [roles, setRoles] = useState(null);
  const [perms, setPerms] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/roles"), api.get("/permissions")]).then(([r, p]) => {
      setRoles(r.data); setPerms(p.data);
      const first = r.data.find((x) => x.code !== "OWNER_CEO") || r.data[0];
      setActive(first); setDraft(first?.permissions || []);
    });
  }, []);

  const permByCode = (code) => perms.find((p) => p.code === code);
  const selectRole = (role) => { setActive(role); setDraft(role.permissions || []); };
  const toggle = (code) => {
    const p = permByCode(code);
    if (p?.sensitive && !isOwnerActor) return toast.error("Hanya OWNER_CEO yang dapat mengubah permission sensitif");
    setDraft(draft.includes(code) ? draft.filter((c) => c !== code) : [...draft, code]);
  };
  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/roles/${active.code}/permissions`, { permissions: draft });
      toast.success(`Permission ${active.name} disimpan`);
      setRoles(roles.map((r) => r.code === active.code ? { ...r, permissions: res.data.permissions } : r));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!roles) return <Loading />;
  const isOwner = active?.code === "OWNER_CEO";
  const modules = [...new Set(perms.map((p) => p.module))];
  const box = (p) => (
    <label key={p.code} title={`${p.name} (${p.code})`} data-testid={`perm-${p.code}`} className="flex items-center justify-center gap-1">
      <Checkbox checked={draft.includes(p.code)} disabled={p.sensitive && !isOwnerActor} onCheckedChange={() => toggle(p.code)} />
      {p.sensitive && <Lock className="h-3 w-3 text-amber-500" />}
    </label>
  );
  const cell = (mod, action) => {
    const list = perms.filter((p) => p.module === mod && lastSeg(p.code) === action);
    return list.length ? list.map(box) : <span className="text-slate-300">–</span>;
  };
  const lainnya = (mod) => perms.filter((p) => p.module === mod && !ACTIONS.includes(lastSeg(p.code)));

  return (
    <div>
      <PageHeader title="Role & Permission" subtitle="Matrix hak akses per modul. 🔒 = permission sensitif (hanya OWNER_CEO yang boleh ubah)." testId="roles-header" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
            {roles.map((r) => (
              <button key={r.code} onClick={() => selectRole(r)} data-testid={`role-${r.code}`}
                className={"flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors " + (active?.code === r.code ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100")}>
                <ShieldCheck className="h-4 w-4 shrink-0" />
                <span className="truncate">{r.name}</span>
                <span className={"ml-auto text-[10px] " + (active?.code === r.code ? "text-indigo-200" : "text-slate-400")}>{r.permissions.includes("*") ? "ALL" : r.permissions.length}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div><h3 className="font-display text-lg font-semibold text-slate-900">{active?.name}</h3><p className="text-xs text-slate-400 font-mono">{active?.code}</p></div>
              {!isOwner && <Button onClick={save} disabled={saving} data-testid="save-roles-btn" className="bg-indigo-600 hover:bg-indigo-500"><Save className="mr-1.5 h-4 w-4" /> {saving ? "..." : "Simpan"}</Button>}
            </div>
            {isOwner ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">Owner / CEO memiliki seluruh permission secara permanen.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase text-slate-400">
                      <th className="p-2 text-left">Modul</th>
                      {ACTIONS.map((a) => <th key={a} className="p-2 text-center">{ACTION_LABEL[a]}</th>)}
                      <th className="p-2 text-left">Lainnya</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((mod) => (
                      <tr key={mod} data-testid={`role-module-${mod}`} className="border-b border-slate-100 align-top">
                        <td className="p-2 font-medium text-slate-700 whitespace-nowrap">{mod}</td>
                        {ACTIONS.map((a) => <td key={a} className="p-2 text-center">{cell(mod, a)}</td>)}
                        <td className="p-2">
                          <div className="flex flex-col gap-1">
                            {lainnya(mod).map((p) => (
                              <label key={p.code} title={p.code} data-testid={`perm-${p.code}`} className="flex items-center gap-1.5 text-xs text-slate-600">
                                <Checkbox checked={draft.includes(p.code)} disabled={p.sensitive && !isOwnerActor} onCheckedChange={() => toggle(p.code)} />
                                {p.sensitive && <Lock className="h-3 w-3 text-amber-500" />}
                                <span className="truncate">{p.name}</span>
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
